import React, { useState, useEffect } from "react";
import { Star, Send, MessageSquare } from "lucide-react";
import { Alert } from "./ui";
import { api, formatApiError } from "../lib/api";
import { StarRating } from "./StarRating";

export default function AppRating({ userId }) {
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [reviewsData, setReviewsData] = useState(null);
  const [myReview, setMyReview] = useState(null);

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const { data } = await api.get("/api/reviews/app");
        setReviewsData(data);
        const mine = data.reviews?.find((r) => r.reviewer_id === userId);
        if (mine) setMyReview(mine);
      } catch (e) {}
    };
    fetchReviews();
  }, [userId]);

  const handleSubmit = async () => {
    if (rating === 0) {
      setError("Pilih rating bintang");
      return;
    }
    setLoading(true);
    setError("");
    try {
      await api.post("/api/reviews", {
        review_type: "APP",
        rating,
        comment: comment || null,
      });
      setDone(true);
      const { data } = await api.get("/api/reviews/app");
      setReviewsData(data);
      const mine = data.reviews?.find((r) => r.reviewer_id === userId);
      if (mine) setMyReview(mine);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card p-6">
      <div className="flex items-center gap-2 mb-3">
        <MessageSquare className="h-5 w-5 text-brand-600" />
        <h2 className="text-brand-800">Rating Aplikasi</h2>
      </div>

      {/* Average rating display */}
      {reviewsData && reviewsData.total_reviews > 0 && (
        <div className="flex items-center gap-3 p-3 bg-brand-50 rounded-xl mb-4">
          <div className="text-center">
            <p className="text-3xl font-extrabold text-brand-800">{reviewsData.average_rating}</p>
            <StarRating rating={reviewsData.average_rating} />
            <p className="text-xs text-brand-400 mt-1">{reviewsData.total_reviews} review</p>
          </div>
        </div>
      )}

      {/* Already reviewed */}
      {(done || myReview) && !error && (
        <div className="p-3 bg-success-50 rounded-xl mb-4">
          <div className="flex items-center justify-between mb-1">
            <p className="font-bold text-success-700 text-sm">Rating Anda</p>
            <StarRating rating={myReview?.rating || rating} />
          </div>
          {myReview?.comment && <p className="text-sm text-success-600">{myReview.comment}</p>}
          <p className="text-xs text-success-500 mt-1">Terima kasih atas rating Anda!</p>
        </div>
      )}

      {/* Form */}
      {!done && !myReview && (
        <>
          {error && <div className="mb-3"><Alert kind="error">{error}</Alert></div>}

          <p className="text-sm text-brand-500 mb-3">Bagaimana pengalaman Anda menggunakan Cariinjasa?</p>

          <div className="flex items-center gap-1 mb-4">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                onClick={() => setRating(star)}
                onMouseEnter={() => setHover(star)}
                onMouseLeave={() => setHover(0)}
                className="focus:outline-none"
                data-testid={`app-rating-star-${star}`}
              >
                <Star
                  className={`h-8 w-8 transition-colors ${
                    star <= (hover || rating)
                      ? "text-amber-400 fill-amber-400"
                      : "text-brand-200"
                  }`}
                />
              </button>
            ))}
            {rating > 0 && (
              <span className="ml-2 text-sm font-medium text-brand-600">
                {rating === 1 ? "Sangat Kurang" : rating === 2 ? "Kurang" : rating === 3 ? "Cukup" : rating === 4 ? "Bagus" : "Sangat Bagus"}
              </span>
            )}
          </div>

          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Ceritakan pengalaman Anda..."
            rows={3}
            className="w-full rounded-xl border-2 border-brand-100 px-3 py-2.5 text-sm focus:border-brand-500 focus:outline-none resize-none bg-white mb-4"
            data-testid="app-rating-comment"
          />

          <button
            onClick={handleSubmit}
            disabled={loading || rating === 0}
            className="btn-primary w-full"
            data-testid="app-rating-submit"
          >
            {loading ? "Mengirim..." : <span className="flex items-center gap-2 justify-center"><Send className="h-4 w-4" /> Kirim Rating</span>}
          </button>
        </>
      )}

      {/* Recent app reviews */}
      {reviewsData && reviewsData.reviews.length > 0 && (
        <div className="mt-4 space-y-2">
          <p className="text-sm font-medium text-brand-500">Review Terbaru</p>
          {reviewsData.reviews.slice(0, 3).map((r) => (
            <div key={r.id} className="p-3 bg-brand-50 rounded-xl">
              <div className="flex items-center justify-between mb-1">
                <p className="font-bold text-brand-800 text-xs">{r.reviewer_name || "Pengguna"}</p>
                <StarRating rating={r.rating} />
              </div>
              {r.comment && <p className="text-xs text-brand-600">{r.comment}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
